import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { apiRequest } from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import API_BASE_URL from '../apiConfig';
import { io } from 'socket.io-client';

export const AuthContext = createContext();





const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [wallets, setWallets] = useState({ usdt: 0, stars: 0, token: 0 });
    const navigate = useNavigate();
    const location = useLocation();
    const [isGameLoading, setIsGameLoading] = useState(false);
    const [socket, setSocket] = useState(null);
    const SOCKET_URL = `${API_BASE_URL}`;

    
useEffect(() => {
  const newSocket = io(SOCKET_URL, {
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 1000,
    timeout: 3000,
    autoConnect: true,
  });

  setSocket(newSocket);

  return () => {
    newSocket.disconnect();
  };
}, []);
    
    const login = useCallback((userData) => {
        setUser(userData);
        setWallets(userData.wallets);
      }, []);
      

    const logout = useCallback(() => {
        setUser(null);
        setWallets({ usdt: 0, stars: 0, token: 0 });
        navigate('/login');
        apiRequest(`${API_BASE_URL}/api/logout`, {
            method: 'POST',
            credentials: 'include',
        });
    }, [navigate]);

    const fetchUserDetails = useCallback(async (retries = 5, delay = 1500) => {
        for (let attempt = 0; attempt < retries; attempt++) {
            try {
                const response = await apiRequest(`${API_BASE_URL}/api/protected`, {
                    method: 'GET',
                    credentials: 'include',
                });
                if ( response.ok) {
                    const data = await response.json();
                    setUser(data.user);
                    setWallets(data.user.wallets);

                    if (socket && socket.connected) {
                        socket.emit('authenticate', { userId: data.user._id });
                    } else  {
                        if(socket){
                        socket.connect();
                        socket.emit('authenticate', { userId: data.user._id });
                        }
                        
                    }
                     return data.user;
                } else if (response.status === 401) {
                    console.warn(`Attempt ${attempt + 1} failed with 401 Unauthorized`);
                    if (attempt < retries - 1) {
                        await new Promise(resolve => setTimeout(resolve, delay));
                    } else {
                        logout();
                    }
                }
            } catch (error) {
                console.error('Error fetching user details:', error);
                break;
            }
        }
    }, [logout]);

    useEffect(() => {
        const publicRoutes = ['/welcome', '/register', '/forgot-password', '/login', '/admin/login', '/admin/dashboard', '/admin/completed-games', '/admin/users'];
        
        const isResetPasswordRoute = location.pathname.startsWith('/reset-password');

        if (!publicRoutes.includes(location.pathname) && !isResetPasswordRoute ) {
            fetchUserDetails();
        }
    }, [fetchUserDetails, location.pathname ]);

        useEffect(() => {
            const handleVisibility = () => {
              
                if (!document.hidden) {
                    if (socket && user){
                        socket.emit('authenticate', { userId: user._id });
                    }
                }
            };
        
            document.addEventListener('visibilitychange', handleVisibility);
            return () => {
                document.removeEventListener('visibilitychange', handleVisibility);
            };
        }, [user]);

    useEffect(() => {
        if (!user) return;

        const handleSocketReconnect = () => {
            if (user && socket) {
                socket.emit('authenticate', { userId: user._id });
            }
        };
    
        socket.on('connect', handleSocketReconnect);

        return () => {
            socket.off('connect', handleSocketReconnect);
        };
    }, [socket, user]); 

    useEffect(() => {
        if (socket) {
            socket.on('walletsUpdated', (newWallets) => {
                setWallets(newWallets);
              });
    
            return () => {
                socket.off('walletsUpdated');
            };
        }
    }, [socket]);

    const updateUserDetails = async () => {
        if (!user || !user._id) {
            console.error('Cannot update user details: User ID is missing.');
            return;
        }

        try {
            const response = await apiRequest(`${API_BASE_URL}/api/user/${user._id}`, {
                method: 'GET',
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setUser({ 
                    ...user, 
                    nickname: data.username, 
                    wallets: data.wallets, 
                    score: data.score, 
                    country: data.country,
                    customAvatar: data.customAvatar,
                    tonWalletAddress: data.tonWalletAddress, 
                    hasSeenWelcomeScreens: data.hasSeenWelcomeScreens,
                    dailyGameStats: data.dailyGameStats

                });
                setWallets(data.wallets);
            } else {
                console.error('Failed to fetch user details:', response.status);
            }
        } catch (error) {
            console.error('Error updating user details:', error.message);
        }
    };
    
  
    const contextValue = useMemo(() => ({
        user,
        wallets,
        setWallets,
        login,
        logout,
        fetchUserDetails,
        updateUserDetails,
        socket ,
        isGameLoading,
        setIsGameLoading
    }), [user, wallets, login, logout, fetchUserDetails, socket,isGameLoading]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export { AuthProvider };
